import React, { useCallback, useMemo, useState } from "react"
import { toast } from "react-toastify"

import { RunsFiltersBar } from "features/tags/filters/RunsFiltersBar"
import { RunsTable } from "features/tags/tables/RunsTable"

import { useDebouncedEffect } from "hooks/useDebouncedEffect"

import { tagsService } from "services/tags/tags.service"
import { TagRule, TagRuleRun } from "services/tags/type"

const DEBOUNCE_MS = 300

type Props = {
  loading: boolean
  setLoading: (v: boolean) => void
  rules: TagRule[]
  onGlobalRefresh: () => Promise<void>
  refreshKey: number
}

export const RunsHistoryTab: React.FC<Props> = ({ loading, setLoading, refreshKey, rules, onGlobalRefresh }) => {
  const [rows, setRows] = useState<TagRuleRun[]>([])

  const [query, setQuery] = useState("")
  const [status, setStatus] = useState<"" | "RUNNING" | "SUCCESS" | "FAILED">("")
  const [mode, setMode] = useState<"" | "BATCH" | "RULE">("")
  const [ruleId, setRuleId] = useState("")

  const ruleOptions = useMemo(() => rules.map((r) => ({ id: r.id, name: r.name })), [rules])

  const load = useCallback(async () => {
    try {
      const res = await tagsService.getRuns({
        page: 1,
        pageSize: 200,
        sortRow: "started_at",
        sortOrder: "desc",
        q: query || undefined,
        status: status || undefined,
        mode: mode || undefined,
        ruleId: ruleId || undefined
      })
      setRows(res?.data ?? [])
    } catch (e: any) {
      toast.error(e?.data?.message ?? e?.message ?? "Failed to load history")
    }
  }, [query, status, mode, ruleId])

  useDebouncedEffect(
    () => {
      load()
    },
    [load, refreshKey],
    DEBOUNCE_MS
  )

  const onRefreshAll = useCallback(async () => {
    setLoading(true)
    try {
      await onGlobalRefresh()
    } finally {
      setLoading(false)
    }
  }, [onGlobalRefresh, setLoading])

  return (
    <>
      <RunsFiltersBar
        loading={loading}
        query={query}
        onQuery={setQuery}
        status={status}
        onStatus={setStatus}
        mode={mode}
        onMode={setMode}
        ruleId={ruleId}
        onRuleId={setRuleId}
        ruleOptions={ruleOptions}
        onRefresh={onRefreshAll}
      />

      <RunsTable rows={rows} loading={loading} />
    </>
  )
}
